import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SocialSharing = ({ property }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [copied, setCopied] = useState(false);

  const propertyUrl = window.location.href;
  const defaultMessage = `Check out this amazing property: ${property.title} in ${property.location}. ${property.configuration} for ${property.type === 'rent' ? 'rent' : 'sale'} at ₹${property.price.toLocaleString()}`;

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: 'MessageCircle',
      color: 'bg-green-500',
      action: () => shareToWhatsApp()
    },
    {
      name: 'Facebook',
      icon: 'Facebook',
      color: 'bg-blue-600',
      action: () => shareToFacebook()
    },
    {
      name: 'Twitter',
      icon: 'Twitter',
      color: 'bg-blue-400',
      action: () => shareToTwitter()
    },
    {
      name: 'LinkedIn',
      icon: 'Linkedin',
      color: 'bg-blue-700',
      action: () => shareToLinkedIn()
    },
    {
      name: 'Email',
      icon: 'Mail',
      color: 'bg-gray-600',
      action: () => shareViaEmail()
    },
    {
      name: 'Copy Link',
      icon: 'Copy',
      color: 'bg-gray-500',
      action: () => copyToClipboard()
    }
  ];

  const shareToWhatsApp = () => {
    const message = customMessage || defaultMessage;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message + '\n\n' + propertyUrl)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareToFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(propertyUrl)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const shareToTwitter = () => {
    const message = customMessage || defaultMessage;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(propertyUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(propertyUrl)}`;
    window.open(linkedinUrl, '_blank', 'width=600,height=400');
  };

  const shareViaEmail = () => {
    const subject = `Property Recommendation: ${property.title}`;
    const body = `${customMessage || defaultMessage}\n\nView property details: ${propertyUrl}`;
    const mailtoUrl = `mailto:${recipientEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(propertyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: property.title,
          text: defaultMessage,
          url: propertyUrl
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      setShowShareModal(true);
    }
  };

  return (
    <>
      {/* Share Button */}
      <Button
        variant="outline"
        iconName="Share"
        iconPosition="left"
        onClick={handleNativeShare}
        className="w-full sm:w-auto"
      >
        Share Property
      </Button>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-lg shadow-modal w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-text-primary">
                  Share Property
                </h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="p-2 hover:bg-muted rounded-lg transition-smooth"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Property Preview */}
              <div className="bg-muted p-4 rounded-lg">
                <div className="flex gap-3">
                  <div className="w-16 h-16 bg-primary/10 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={property.images?.[0] || '/assets/images/no_image.png'}
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-text-primary truncate">
                      {property.title}
                    </h4>
                    <p className="text-sm text-text-secondary truncate">
                      {property.location}
                    </p>
                    <p className="text-sm font-medium text-primary">
                      ₹{property.price.toLocaleString()} • {property.configuration}
                    </p>
                  </div>
                </div>
              </div>

              {/* Custom Message */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Custom Message (Optional)
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder={defaultMessage}
                />
              </div>

              {/* Email Recipient (for email sharing) */}
              <Input
                label="Email To (Optional)"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="Enter recipient's email"
              />

              {/* Share Options */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Share via
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {shareOptions.map((option) => (
                    <button
                      key={option.name}
                      onClick={option.action}
                      className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-muted transition-smooth"
                    >
                      <div className={`w-10 h-10 ${option.color} rounded-full flex items-center justify-center`}>
                        <Icon name={option.icon} size={20} color="white" />
                      </div>
                      <span className="text-xs text-text-secondary">
                        {option.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Copy Link Section */}
              <div className="bg-muted p-4 rounded-lg">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Property Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={propertyUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary"
                  />
                  <Button
                    variant={copied ? "success" : "outline"}
                    size="sm"
                    iconName={copied ? "Check" : "Copy"}
                    onClick={copyToClipboard}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Quick Share Stats */}
              <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="TrendingUp" size={16} className="text-primary" />
                  <span className="font-medium text-text-primary">Sharing Stats</span>
                </div>
                <div className="text-sm text-text-secondary space-y-1">
                  <div>This property has been shared 24 times this week</div>
                  <div>Most shared via WhatsApp (67%)</div>
                  <div>Average response rate: 23%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SocialSharing;